// Analytics Service for fetching dashboard data

import { DashboardData, DateRangeData, TopService } from '../types';
import { API_BASE_URL } from '../config/api';
import { authFetch } from './session';

const API_URL = API_BASE_URL;



// Response structure
interface ApiResponse<T> {
  status: string;
  message?: string | null;
  data: T;
}

// Helper function to format date for API
const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to handle API authentication
const fetchWithAuth = async <T>(url: string): Promise<ApiResponse<T>> => {
  const response = await authFetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (response.status === 401) {
    // Final: authFetch already tried to renew the session and fired the
    // auth-error event (AuthContext redirects to /login). Going through it is
    // what stops the dashboard's 30s poll from hammering a dead token for
    // hours (live on 2026-07-30, when this wrapper had no 401 handling).
    throw new Error('Authentication expired');
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

const analyticsService = {
  getMonthlyAnalytics: async (selectedMonth: Date, source?: string): Promise<DashboardData> => {
    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const formattedMonth = month.toString().padStart(2, '0');
      
      let url = `${API_URL}/analytics/dashboard?month=${year}-${formattedMonth}`;
      if (source) {
        url += `&source=${source}`;
      }
      
      const response = await fetchWithAuth<DashboardData>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching monthly analytics:', error);
      throw error;
    }
  },

  getAppointmentsForDate: async (date: Date): Promise<DashboardData> => {
    try {
      const formattedDate = formatDateForApi(date);
      const response = await fetchWithAuth<DashboardData>(`${API_URL}/analytics/appointments?date=${formattedDate}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  },

  getServicesForDate: async (date: Date): Promise<DashboardData> => {
    try {
      const formattedDate = formatDateForApi(date);
      const response = await fetchWithAuth<DashboardData>(`${API_URL}/analytics/services?date=${formattedDate}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  },

  getAvailableDateRanges: async (): Promise<DateRangeData> => {
    try {
      const response = await fetchWithAuth<DateRangeData>(`${API_URL}/analytics/available-dates`);
      return response.data;
    } catch (error) {
      console.error('Error fetching available dates:', error);
      throw error;
    }
  },

  getTopServices: async (source?: string): Promise<TopService[]> => {
    try {
      let url = `${API_URL}/analytics/kpi/top-services`;
      if (source) {
        url += `?source=${source}`;
      }
      const response = await fetchWithAuth<TopService[]>(url);
      return response.data ?? [];
    } catch (error) {
      console.error('Error fetching top services:', error);
      throw error;
    }
  }
};

export default analyticsService;